<script setup lang="ts">

import CustomButton from "@/components/Button/Button.vue";
import Drawer from "@/components/MenuDrawer/MenuDrawer.vue";
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import {useWindowSize} from "@vueuse/core";
import { useRouteHelpers } from "@/composables/useRouteHelp";
import { useRoute, useRouter} from "vue-router";
import {useFavoritesStore} from "@/stores/favorites.js";
import FavoritesDropdown from "@/components/FavoritesDropdown/FavoritesDropdown.vue";
import {useBlogStore} from "@/stores/blogStore.js";



const router = useRouter();
const route = useRoute();

const favoritesStore = useFavoritesStore();
const blogStore = useBlogStore();

const showFavorites = ref(false)
const addedToFavorites = ref(false);
const favoritesDropdown = ref();


const isDrawerOpen = ref(false);

const openDrawer = () => (isDrawerOpen.value = true);
const closeDrawer = () => (isDrawerOpen.value = false);

const { width } = useWindowSize();
const isMobileSmall = computed(() => width.value <= 1336);

const { isYachtsPage, isBlogPage, isDestinationsPage, isGuidePage, isAboutPage, isImprintPage } = useRouteHelpers();


const scrolled = ref(false);
const showLimitMessage = ref(false)



const handleScroll = () => {
  scrolled.value = window.scrollY > 50
}


function inquireYachts() {
  showFavorites.value=false
  favoritesDropdown.value?.handleClose();
  router.push('/yachtsInquiry')
}

function handleScrollClose() {
  favoritesDropdown.value?.handleClose()
}

function handleDropdownVisible(visible: boolean) {
  if (visible) {
    window.addEventListener('scroll', handleScrollClose, { once: true })
  } else {
    window.removeEventListener('scroll', handleScrollClose)
  }
}

const showSolidHeader = computed(() =>
    (isBlogPage.value || isImprintPage.value)
);

onMounted(() => {
  window.addEventListener('scroll', handleScroll)

})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('scroll', handleScrollClose)

})

watch(() => favoritesStore.favorites.length, (newVal, oldVal) => {
  if (newVal > oldVal) {
    addedToFavorites.value = true
    setTimeout(() => {
      addedToFavorites.value = false
    }, 3000)
  }
})

watch(
    () => favoritesStore.limitReached,
    (newVal) => {
      if (newVal) {
        showLimitMessage.value = true
        setTimeout(() => {
          showLimitMessage.value = false
          favoritesStore.limitReached = false  // reset
        }, 3000)
      }
    }
)



</script>


<template>
  <div
      :class="[
      'header flex items-center overflow-visible absolute w-full header-light top-0 z-40 transition-all duration-700 ease-in-out',

      { 'bg-white sticky border-bw-grey': scrolled  },
      { 'bg-transparent sticky border-bw-grey': showSolidHeader && !scrolled }
    ]"
  >
    <div class="w-screen sticky z-50">
      <div class="container flex flex-row justify-between items-center h-18 mx-auto">
        <!-- Logo Section -->
        <div class="flex items-center gap-x-6 flex-shrink-0">
          <router-link :to="`/`" class="logo-link">
            <img v-if="!scrolled && !showSolidHeader"
                src="@/assets/logo_luxury_yachts.svg"
                class="cursor-pointer h-10"
                alt="Cosmos Logo"
            />
            <img v-else
                 src="@/assets/logo_luxury_yachts_red.png"
                 class="cursor-pointer h-10"
                 alt="Cosmos Logo"
            />
          </router-link>
        </div>

        <!-- Center section -->
        <div v-if="!isMobileSmall" class="container flex flex-grow gap-4 justify-center w-full md:flex">
          <div :class="[
            'flex justify-between items-center gap-4 font-bold responsive-body-medium',
            scrolled || showSolidHeader ? 'text-primary-darkBlue' : 'text-white'
          ]">

            <RouterLink
                to="/yachts"
                :class="['flex items-center justify-center px-4 h-[60px] hover:h-[70px] transition-all duration-200 text-nowrap',
                scrolled || showSolidHeader ? 'hover:bg-primary-darkBlue hover:text-white hover:border-t-4 border-primary-violet' : 'hover:bg-white hover:text-primary-darkBlue hover:border-t-4 border-primary-violet',
                isYachtsPage ? 'h-[70px] border-t-4 border-primary-violet' : ''
                ]"
            >
              YACHTS
            </RouterLink>

            <RouterLink
                :to="`/destinations`"
                :class="['flex items-center justify-center px-4 h-[60px] hover:h-[70px] transition-all duration-200 text-nowrap',
                scrolled || showSolidHeader ? 'hover:bg-primary-darkBlue hover:text-white hover:border-t-4 border-primary-violet' : 'hover:bg-white hover:text-primary-darkBlue hover:border-t-4 border-primary-violet ',
                isDestinationsPage ? 'h-[70px] border-t-4 border-primary-violet' : ''
                ]"
            >
              DESTINATIONS
            </RouterLink>
            <RouterLink
                :to="`/magazine`"
                :class="['flex items-center justify-center px-4 h-[60px] hover:h-[70px] transition-all duration-200 text-nowrap',
                scrolled || showSolidHeader ? 'hover:bg-primary-darkBlue hover:text-white hover:border-t-4 border-primary-violet' : 'hover:bg-white hover:text-primary-darkBlue hover:border-t-4 border-primary-violet ',
                isBlogPage ? 'h-[70px]  border-t-4 border-primary-violet' : ''

                ]"
            >
              MAGAZINE
            </RouterLink>
            <RouterLink
                :to="`/guide`"
                :class="['flex items-center justify-center px-4 h-[60px] hover:h-[70px] transition-all duration-200 text-nowrap',
                scrolled || showSolidHeader ? 'hover:bg-primary-darkBlue hover:text-white hover:border-t-4 border-primary-violet' : 'hover:bg-white hover:text-primary-darkBlue hover:border-t-4 border-primary-violet ',
                isGuidePage ? 'h-[70px]  border-t-4 border-primary-violet' : ''

                ]"
            >
              CHARTER GUIDE
            </RouterLink>
            <RouterLink
                :to="`/aboutUs`"
                :class="['flex items-center justify-center px-4 h-[60px] hover:h-[70px] transition-all duration-200 text-nowrap',
                scrolled || showSolidHeader ? 'hover:bg-primary-darkBlue hover:text-white hover:border-t-4 border-primary-violet' : 'hover:bg-white hover:text-primary-darkBlue hover:border-t-4 border-primary-violet ',
                isAboutPage ? 'h-[70px]  border-t-4 border-primary-violet' : ''

                ]">
              ABOUT US
            </RouterLink>
          </div>
        </div>

        <!-- Right Section -->
        <div v-if="!isMobileSmall" class="flex items-center gap-3 md:gap-6 flex-shrink-0 ">
          <div>
            <div class="relative inline-block">

              <el-dropdown ref="favoritesDropdown" placement="bottom-start" trigger="click" @visible-change="handleDropdownVisible">
                <el-button>
                  <div
                    :class="[
                      'flex items-center gap-2.5 responsive-body-large text-primary-darkBlue',
                      scrolled || showSolidHeader ? 'text-primary-darkBlue' : 'text-white'
                    ]"
                  >
                    <img
                        v-if="!scrolled && !showSolidHeader"
                        src="@/assets/icons/favorite_white.svg"
                        alt="Favorite logo"
                    />
                    <img
                        v-else
                        src="@/assets/icons/favorite_blue.svg"
                        alt="Favorite logo"
                    />
                    <span>Favorites ({{ favoritesStore.favorites.length }})</span>
                  </div>
                </el-button>

                <!-- Dropdown content -->
                  <template #dropdown>
                    <FavoritesDropdown @inquire="inquireYachts" />
                  </template>
              </el-dropdown>

              <!-- Popup notification -->
              <transition name="fade">
                <div
                    v-if="addedToFavorites"
                    class="absolute mt-2 bg-primary-darkBlue text-white border-2 border-white shadow-lg rounded px-4 py-2 responsive-body-small z-50"
                    ref="popupRef"
                >
                  Added to favorites
                </div>
              </transition>


              <!-- Only up to 5 yachts notification -->
              <transition name="fade">
                <div
                    v-if="showLimitMessage"
                    class="absolute mt-2 bg-background-beige text-primary-darkRed border-2 border-primary-darkRed shadow-lg rounded px-2 py-2 responsive-body-small text-center z-50"
                    ref="limitPopupRef"
                >
                  <div class="flex align-center gap-0.5">
                    <img src="@/assets/icons/warning.png" alt="Warning" class="h-4 w-4" />
                    You can only add up to 5 favorites.
                  </div>
                </div>
              </transition>

            </div>

          </div>
          <CustomButton
              type="yellow"
              :shadow="false"
              size="medium"
              class="py-2 px-4"
              @click="router.push('/inquiry')"
          >
            INQUIRY
          </CustomButton>
        </div>


        <!-- MOBILE -->

        <div v-if="isMobileSmall" class="flex gap-3">
          <!-- Favorites -->
          <div class="relative inline-block">

            <el-dropdown ref="favoritesDropdown" placement="bottom-start" @visible-change="handleDropdownVisible">
              <el-button>
                <div
                  :class="`relative transition-all ${
                          favoritesStore.favorites.length < 1
                            ? 'cursor-not-allowed'
                            : 'cursor-pointer'
                        } flex items-center gap-2`"
                  :disabled="favoritesStore.favorites.length  <= 0"
                >

                  <img v-if="!scrolled && !showSolidHeader"
                       src="@/assets/icons/favorite_white.svg" alt="Favorite logo"
                       class="transition-transform duration-300 w-6 h-6 "
                       :class="favoritesStore.favorites.length >= 1 ? 'opacity-100 filter' : 'opacity-40'"
                  >
                  <img v-else
                       src="@/assets/icons/favorite_blue.svg" alt="Favorite logo"
                       class="transition-transform duration-300 w-6 h-6 "
                       :class="favoritesStore.favorites.length >= 1 ? 'opacity-100 filter' : 'opacity-40'"
                  >

                  <div
                      class="absolute right-[-9px] top-[-4px] w-3.5 h-3.5 bg-primary-darkRed text-white responsive-body-xsmall rounded-full flex items-center justify-center"
                  >
                    {{ favoritesStore.favorites.length }}
                  </div>

                </div>

              </el-button>
              <template #dropdown>
                <FavoritesDropdown @inquire="inquireYachts" />
              </template>
            </el-dropdown>

            <!-- Popup notification -->

            <transition name="fade">
              <div
                  v-if="addedToFavorites"
                  class="absolute mt-2 bg-primary-darkBlue text-white border-2 border-white shadow-lg rounded py-2 responsive-body-xsmall text-center z-50 w-32"
                  ref="popupRef"
              >
                Added to favorites
              </div>
            </transition>


            <transition name="fade">
              <div
                  v-if="showLimitMessage"
                  class="absolute top-full right-0 mt-2 bg-background-beige text-primary-darkRed border-2 border-primary-darkRed shadow-lg rounded px-1 py-2 responsive-body-small z-50 w-[150px]"
                  ref="limitPopupRef"
              >
                <div class="flex items-center gap-2">
                  <img src="@/assets/icons/warning.png" alt="Warning" class="h-4 w-4 shrink-0" />
                  <span class="responsive-body-small">You can only add up to 5 favorites.</span>
                </div>
              </div>
            </transition>
          </div>

          <!-- Drawer Menu -->
          <div v-if="isMobileSmall"
               :class="[
                 'w-12 h-7 flex justify-center items-center rounded-11xl cursor-pointer transition-all',
                 scrolled || showSolidHeader ? 'border border-black' : 'border-2 border-white'
                 ]"
               @click="openDrawer"
          >
            <img class="drawer-menu__img"
                 src="@/assets/icons/Menu_16x16.svg" alt="Menu"
                 :class="{ 'scrolled-black': scrolled || showSolidHeader }"

            />
          </div>
        </div>

      </div>
    </div>
  </div>
  <Drawer :isOpen="isDrawerOpen" @close="closeDrawer"/>
</template>

<style scoped>
.header-light .drawer-menu__img {
  filter: contrast(0%) brightness(2);
}

.drawer-menu__img.scrolled-black {
  filter: brightness(0) saturate(100%);

}


:deep(.el-button){
  border: none !important;
  background-color: transparent !important;
}

:deep(.el-button:hover),
:deep(.el-button:focus),
:deep(.el-button:active) {
  background-color: transparent !important;
  box-shadow: none !important;
  border: none !important;
  outline: none !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}



</style>
